<template>
  <div class="reference-register-page">
    <section class="reference-register-hero" aria-hidden="true"></section>

    <header class="reference-register-nav">
      <button type="button" class="reference-register-back" aria-label="Back" @click="back">
        <img :src="backIcon" alt="">
      </button>
      <span>CRUSH</span>
    </header>

    <h1 class="reference-register-title">{{ $t('register.register') }}</h1>

    <form class="reference-register-form" @submit.prevent="doRegister">
      <div class="reference-field-block">
        <input
          v-model.trim="username"
          class="reference-register-input"
          type="text"
          autocomplete="username"
          maxlength="20"
          :placeholder="$t('login.usernamePls')"
        >
        <p>6-20 characters, letters and numbers</p>
      </div>

      <div class="reference-field-block">
        <div class="reference-password-field">
          <input
            v-model="password"
            class="reference-register-input"
            :type="passwordType"
            autocomplete="new-password"
            maxlength="12"
            :placeholder="$t('login.passwordPls')"
          >
          <button type="button" class="reference-password-toggle" aria-label="Toggle password visibility" @click="switchPasswordType">
            <van-icon :name="passwordType === 'password' ? 'closed-eye' : 'eye-o'" />
          </button>
        </div>
        <p>6-12 characters (letters, numbers or symbols)</p>
      </div>

      <div class="reference-field-block reference-captcha-block">
        <div class="reference-captcha-row">
          <input
            v-model.trim="verificationCode"
            class="reference-register-input"
            type="text"
            autocomplete="off"
            maxlength="5"
            placeholder="Please enter verification code"
          >
          <button type="button" class="reference-captcha" title="Refresh verification code" @click="refreshCaptcha">
            {{ captchaText }}
          </button>
        </div>
        <p>Please enter 5-digit alphanumeric verification code</p>
      </div>

      <div class="reference-field-block">
        <input
          v-model.trim="code"
          class="reference-register-input"
          type="text"
          autocomplete="off"
          :placeholder="$t('register.codePls')"
        >
        <p>Please enter agent invitation code</p>
      </div>

      <label class="reference-agreement">
        <input v-model="checked" type="checkbox">
        <span class="reference-checkbox" aria-hidden="true">
          <van-icon v-if="checked" name="success" />
        </span>
        <span>I am over 18 and agree to the 'Registration Terms'</span>
      </label>

      <button
        type="submit"
        class="reference-register-button"
        :class="{ active: btnState }"
        :disabled="isLoading"
      >
        <span v-if="!isLoading">{{ $t('register.register') }}</span>
        <span v-else>Registering...</span>
      </button>

      <button type="button" class="reference-login-link" @click="toLogin">
        Already have an account? {{ $t('login.doLogin') }}
      </button>
    </form>
  </div>
</template>

<script>
import backIcon from '@/assets/img/reference-login/back.png'

export default {
  name: 'ReferenceRegister',
  data() {
    return {
      checked: true,
      username: '',
      code: '',
      password: '',
      passwordType: 'password',
      verificationCode: '',
      captchaText: '',
      isLoading: false,
      backIcon
    }
  },
  computed: {
    btnState() {
      return Boolean(this.username && this.password && this.verificationCode && this.code && this.checked)
    }
  },
  methods: {
    switchPasswordType() {
      this.passwordType = this.passwordType === 'password' ? 'text' : 'password'
    },
    refreshCaptcha() {
      const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
      this.captchaText = Array.from({ length: 5 }, () => {
        return alphabet[Math.floor(Math.random() * alphabet.length)]
      }).join('')
      this.verificationCode = ''
    },
    toLogin() {
      if (this.$route.path !== '/Login') this.$router.push('/Login')
    },
    back() {
      if (window.history.length > 1) this.$router.go(-1)
      else this.toLogin()
    },
    async doRegister() {
      if (this.isLoading) return
      if (!this.username || this.username.length < 6 || this.username.length > 20) {
        this.$toast('Username must be 6 to 20 characters long')
        return
      }
      if (!/^[A-Za-z0-9]+$/.test(this.username)) {
        this.$toast('Username can only contain letters and numbers')
        return
      }
      if (!this.password) {
        this.$toast(this.$t('login.passwordPls'))
        return
      }
      if (this.password.length < 6 || this.password.length > 12) {
        this.$toast(this.$t('registerPasswordLength'))
        return
      }
      if (this.verificationCode.toUpperCase() !== this.captchaText) {
        this.$toast('Incorrect verification code')
        this.refreshCaptcha()
        return
      }
      if (!this.code) {
        this.$toast(this.$t('register.codePls'))
        return
      }
      if (!this.checked) {
        this.$toast(this.$t('register.xieyiPls'))
        return
      }

      this.isLoading = true
      try {
        const res = await this.$http({
          method: 'post',
          url: 'member_register',
          data: {
            username: this.username,
            password: this.password,
            paypassword: this.password,
            code: this.code,
            lang: localStorage.getItem('lang') || localStorage.getItem('change-language') || 'en'
          }
        })

        if (res.code === 200 || res.code === 0) {
          this.$toast.success(res.msg || 'Registration successful')
          const token = res.data && typeof res.data === 'object'
            ? (res.data.id || res.data.token)
            : res.data
          if (token) this.$store.commit('setToken', token)
          if (res.data && typeof res.data === 'object') this.$store.commit('setUserInfoValue', res.data)
          this.$router.push(token ? '/Mine' : '/Login')
          return
        }

        this.$toast.fail(res.msg || 'Registration failed. Please try again')
        this.refreshCaptcha()
      } catch (error) {
        this.$toast.fail('Registration request failed. Please try again')
        this.refreshCaptcha()
      } finally {
        this.isLoading = false
      }
    }
  },
  created() {
    this.refreshCaptcha()
    if (localStorage.getItem('token')) this.$router.replace('/Mine')
  }
}
</script>

<style lang="less" scoped>
@import "../../assets/css/basenew.css";

.reference-register-page {
  position: relative;
  z-index: 2;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  overflow-x: hidden;
  color: #f4f1ee;
  background: #1f1d20;
}

.reference-register-hero {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 70vw;
  max-height: 330PX;
  background:
    linear-gradient(rgba(18, 10, 12, .55), rgba(20, 17, 19, .62)),
    url('../../assets/img/reference-login/login.jpg') center top / auto 100% no-repeat;
}

.reference-register-nav {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  height: 12vw;
  padding: max(1.333vw, env(safe-area-inset-top)) 5.333vw 0;
  color: #f6e6d7;
  font-size: 3.733vw;
}

.reference-register-back {
  display: grid;
  width: 8vw;
  height: 8vw;
  margin-right: 1.333vw;
  padding: 0;
  place-items: center;
  border: 0;
  background: transparent;
}

.reference-register-back img {
  width: 4.8vw;
  height: auto;
}

.reference-register-title {
  position: relative;
  z-index: 2;
  margin: 3.2vw 0 0;
  color: #fff;
  font-size: 9.6vw;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 1.2vw;
  text-align: center;
  text-shadow: 0 3PX 14PX rgba(0, 0, 0, .42);
}

.reference-register-form {
  position: relative;
  z-index: 2;
  margin-top: 10.667vw;
  padding: 0 max(5.333vw, env(safe-area-inset-right)) max(8vw, env(safe-area-inset-bottom)) max(5.333vw, env(safe-area-inset-left));
}

.reference-field-block {
  margin-bottom: 3.733vw;
}

.reference-register-input {
  display: block;
  width: 100%;
  height: 14.4vw;
  padding: 0 5.333vw;
  border: .267vw solid #776858;
  border-radius: 1.6vw;
  outline: 0;
  color: #eee;
  background: linear-gradient(120deg, #222, #2e2e2e);
  box-sizing: border-box;
  font-size: 4vw;
  line-height: 1;
  text-align: center;
  transition: border-color .2s ease, box-shadow .2s ease;
}

.reference-register-input:focus {
  border-color: #e6b98a;
  box-shadow: 0 0 0 .8vw rgba(230, 185, 138, .12);
}

.reference-register-input::placeholder {
  color: #d1ced0;
  opacity: 1;
}

.reference-field-block > p {
  margin: 1.6vw 3.2vw 0;
  color: #d2ced0;
  font-size: 2.933vw;
  line-height: 1.4;
}

.reference-password-field { position: relative; }

.reference-password-field .reference-register-input {
  padding-right: 12vw;
  padding-left: 12vw;
}

.reference-password-toggle {
  position: absolute;
  top: 0;
  right: 2.667vw;
  display: grid;
  width: 10.667vw;
  height: 14.4vw;
  padding: 0;
  place-items: center;
  border: 0;
  color: #aaa6a8;
  background: transparent;
  font-size: 5.333vw;
}

.reference-captcha-block { padding-top: 4vw; }

.reference-captcha-row {
  display: flex;
  align-items: center;
  gap: 2.667vw;
}

.reference-captcha-row .reference-register-input {
  flex: 1;
  min-width: 0;
}

.reference-captcha {
  flex: 0 0 22vw;
  height: 10vw;
  overflow: hidden;
  border: .267vw solid #c9c3bc;
  color: #29252a;
  background:
    repeating-linear-gradient(13deg, transparent 0 1.6vw, rgba(70, 60, 60, .2) 1.867vw 2.133vw),
    #f4f0ed;
  font-family: Georgia, serif;
  font-size: 4.267vw;
  font-style: italic;
  letter-spacing: .8vw;
  transform: rotate(-1deg);
}

.reference-agreement {
  display: flex;
  align-items: center;
  margin: 5.333vw 2.667vw 0;
  color: #f3eeee;
  font-size: 3.2vw;
  line-height: 1.45;
  cursor: pointer;
}

.reference-agreement > input {
  position: absolute;
  width: 1PX;
  height: 1PX;
  opacity: 0;
}

.reference-checkbox {
  display: grid;
  flex: 0 0 6.4vw;
  width: 6.4vw;
  height: 6.4vw;
  margin-right: 2.667vw;
  place-items: center;
  border: .267vw solid #8c8588;
  border-radius: 50%;
  color: #68302c;
  background: transparent;
  box-sizing: border-box;
  font-size: 4.267vw;
}

.reference-agreement > input:checked + .reference-checkbox {
  border-color: #f0c69e;
  background: linear-gradient(#fde3ca, #e7b486);
}

.reference-register-button {
  width: 100%;
  height: 11.2vw;
  margin-top: 8vw;
  padding: 0;
  border: 0;
  border-radius: 8vw;
  color: #68302c;
  background: linear-gradient(#fde3ca, #e7b486);
  font-size: 4vw;
  font-weight: 700;
  box-shadow: 0 2.667vw 6.667vw rgba(0, 0, 0, .2);
}

.reference-register-button:disabled { opacity: .72; }

.reference-login-link {
  display: block;
  margin: 2.667vw auto 0;
  padding: 1.333vw;
  border: 0;
  color: #bfa77c;
  background: transparent;
  font-size: 3.333vw;
}

@media (min-width: 431px) {
  .reference-register-page {
    max-width: 430PX;
    width: 430PX;
    box-shadow: 0 0 50PX rgba(0, 0, 0, .38);
  }
  .reference-register-hero { height: 300PX; }
  .reference-register-nav { height: 58PX; padding: 0 24PX; font-size: 16PX; }
  .reference-register-back { width: 44PX; height: 44PX; margin-right: 6PX; }
  .reference-register-back img { width: 24PX; }
  .reference-register-title { margin-top: 23PX; font-size: 52PX; letter-spacing: 6PX; }
  .reference-register-form { margin-top: 45PX; padding-right: 28PX; padding-left: 28PX; }
  .reference-register-input { height: 64PX; padding: 0 22PX; border-width: 1PX; border-radius: 8PX; font-size: 20PX; }
  .reference-field-block { margin-bottom: 24PX; }
  .reference-field-block > p { margin: 10PX 16PX 0; font-size: 14PX; }
  .reference-password-field .reference-register-input { padding-right: 60PX; padding-left: 60PX; }
  .reference-password-toggle { right: 10PX; width: 50PX; height: 64PX; font-size: 28PX; }
  .reference-captcha-block { padding-top: 24PX; }
  .reference-captcha-row { gap: 12PX; }
  .reference-captcha { flex-basis: 104PX; height: 46PX; font-size: 21PX; letter-spacing: 3PX; }
  .reference-agreement { margin: 30PX 12PX 0; font-size: 16PX; }
  .reference-checkbox { flex-basis: 34PX; width: 34PX; height: 34PX; margin-right: 12PX; font-size: 22PX; }
  .reference-register-button { height: 56PX; margin-top: 42PX; border-radius: 32PX; font-size: 20PX; }
  .reference-login-link { margin-top: 16PX; padding: 8PX; font-size: 15PX; }
}

@media (max-width: 359px) {
  .reference-register-form {
    padding-right: 5.333vw;
    padding-left: 5.333vw;
  }

  .reference-field-block > p {
    margin-right: 2.133vw;
    margin-left: 2.133vw;
  }

  .reference-agreement {
    align-items: flex-start;
    font-size: 3.467vw;
  }
}
</style>
